import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useApi } from '../Api';
import { CharitableOrganizationDto } from '../types/CharitableOrganization';
import { CHARITABLE_ORGANIZATION_TYPES, CHARITABLE_ORGANIZATION_JURISDICTIONS } from '../constants/charitableOrganization';
import { formatTaxId, unformatTaxId } from '../utils/formatting';
import CustomInput from '../controls/CustomInput';
import { FormSelect } from '../controls/FormSelect';

const CharitableOrganizationFormPage: React.FC = () => {
  const { familyId } = useParams<{ familyId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const api = useApi();

  const [formData, setFormData] = useState<CharitableOrganizationDto>({
    id: 0,
    name: '',
    type: '',
    jurisdiction: '',
    taxId: '',
    dateOfFormation: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    const loadCharitableOrganization = async (charitableOrganizationId: number) => {
      try {
        setIsLoading(true);
        const response = await api.get(`/api/charitableorganization/getcharitableorganization?charitableOrganizationId=${charitableOrganizationId}`);
        const charitableOrg = response.data.charitableOrganization;

        // Format the tax ID for display
        setFormData({
          ...charitableOrg,
          taxId: formatTaxId(charitableOrg.taxId)
        });
      } catch (error) {
        console.error('Error loading charitable organization:', error);
      } finally {
        setIsLoading(false);
      }
    };

    // Check if we're editing an existing charitable organization
    const charitableOrganizationId = new URLSearchParams(location.search).get('charitableOrganizationId');
    if (charitableOrganizationId) {
      setIsEditing(true);
      loadCharitableOrganization(parseInt(charitableOrganizationId));
    }
  }, [location.search, api]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!familyId) return;

    try {
      setIsLoading(true);

      // Prepare data with unformatted Tax ID
      const submitData = {
        ...formData,
        taxId: unformatTaxId(formData.taxId)
      };

      if (isEditing) {
        await api.put(`/api/charitableorganization/updatecharitableorganization?familyId=${familyId}`, submitData);
      } else {
        await api.post(`/api/charitableorganization/createcharitableorganization?familyId=${familyId}`, submitData);
      }

      navigate(`/family-management/${familyId}`);
    } catch (error) {
      console.error('Error saving charitable organization:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (familyId) {
      navigate(`/family-management/${familyId}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Header Card */}
          <div className="bg-white rounded-xl shadow-md">
            <div className="px-6 py-8">
              <h1 className="text-3xl font-bold mb-2">
                {isEditing ? 'Edit Charitable Organization' : 'Add New Charitable Organization'}
              </h1>
              <p className="text-lg">
                {isEditing ? 'Update charitable organization information' : 'Enter charitable organization information to add to the family'}
              </p>
            </div>
          </div>

          {/* Basic Information Card */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <CustomInput
                label="Organization Name"
                name="name"
                value={formData.name}
                onChange={(val) => setFormData(prev => ({ ...prev, name: val }))}
                required
              />

              <FormSelect
                label="Organization Type"
                value={formData.type || null}
                options={Object.entries(CHARITABLE_ORGANIZATION_TYPES).map(([key, value]) => ({ value: key, label: value as string }))}
                onChange={(val) => setFormData(prev => ({ ...prev, type: (val as string) ?? '' }))}
                required
              />

              <FormSelect
                label="Jurisdiction"
                value={formData.jurisdiction || null}
                options={Object.entries(CHARITABLE_ORGANIZATION_JURISDICTIONS).map(([key, value]) => ({ value: key, label: value as string }))}
                onChange={(val) => setFormData(prev => ({ ...prev, jurisdiction: (val as string) ?? '' }))}
                required
              />

              <CustomInput
                label="Tax ID #"
                name="taxId"
                value={formData.taxId}
                onChange={(val) => setFormData(prev => ({ ...prev, taxId: formatTaxId(val) }))}
                placeholder="XX-XXXXXXX"
                required
              />

              <CustomInput
                label="Date of Formation"
                name="dateOfFormation"
                type="date"
                value={formData.dateOfFormation}
                onChange={(val) => setFormData(prev => ({ ...prev, dateOfFormation: val }))}
                required
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleCancel}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary"
            >
              {isLoading ? 'Saving...' : (isEditing ? 'Update Charitable Organization' : 'Add Charitable Organization')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CharitableOrganizationFormPage;
